/**
 * Returns true for paths that are served as pure server-rendered HTML with no
 * Vike client bundle (injectScriptsAt: null). Vike's soft navigate() and React
 * hydration must never be used for these routes — the page must always be
 * fetched fresh from the server.
 *
 * Matched sets:
 *   - Landing pages:  `/en`, `/fi`, `/sv` (`/` 301 → `/en`)
 *   - Resource hubs:  /en/resources/*, /fi/tyoelama/*, /sv/arbetsliv/*
 *   - Company pages (English slugs):  /about, /partnerships, /privacy, /terms,
 *                                     /accessibility-statement
 *   - Company pages (Finnish slugs):  /fi/tietoa-jobcrawlsista, /fi/kumppanuudet,
 *                                     /fi/tietosuoja, /fi/toimitusperiaatteet, /fi/yhteystiedot,
 *                                     /fi/saavutettavuusseloste
 *   - Company pages (Swedish slugs):  /sv/om-jobcrawls, /sv/partnerskap,
 *                                     /sv/integritet, /sv/redaktionella-riktlinjer, /sv/kontakt,
 *                                     /sv/tillganglighetsutlatande
 *   - Terms: English only at /en/terms (no /fi/kayttoehdot or /sv/villkor)
 */
export function isStaticDestination(path: string): boolean {
  // Landing pages: /en, /fi, /sv (with optional trailing slash). Root / is a CDN redirect.
  if (/^\/(en|fi|sv)\/?$/.test(path)) return true;

  // Resource hubs and all sub-pages (each language tree has injectScriptsAt: null)
  if (/^\/(en\/resources|fi\/tyoelama|sv\/arbetsliv)(\/|$)/.test(path)) return true;

  // Job-search guide hubs (prerendered in web-static)
  if (/^\/(en\/job-search|fi\/tyonhaku|sv\/jobbsok)(\/|$)/.test(path)) return true;

  // Hosted employer careers pages (TEC-488): SSR with clientHooks:false — no client bundle,
  // so a soft navigate() into them would white-screen.
  if (/^\/(en\/careers|fi\/ura|sv\/karriar)(\/|$)/.test(path)) return true;

  // Company/policy pages — English slugs with optional locale prefix
  if (
    /^\/(en|fi|sv)?\/?(?:about|partnerships|privacy|terms|editorial-standards|contact|accessibility-statement)(\/|$)/.test(
      path,
    )
  )
    return true;

  // Company/policy pages — Finnish localised slugs
  if (
    /^\/fi\/(tietoa-jobcrawlsista|kumppanuudet|tietosuoja|toimitusperiaatteet|yhteystiedot|saavutettavuusseloste)(\/|$)/.test(
      path,
    )
  )
    return true;

  // Company/policy pages — Swedish localised slugs
  if (
    /^\/sv\/(om-jobcrawls|partnerskap|integritet|redaktionella-riktlinjer|kontakt|tillganglighetsutlatande)(\/|$)/.test(
      path,
    )
  )
    return true;

  return false;
}

/**
 * Pages that DO ship the normal client bundle and must hydrate on initial load (menu, language
 * switcher and every other island depend on it), but whose data comes from `+data.server.ts`
 * only, so a soft client-side navigate() into them would render with no data. Navigation into
 * them must be a full page load; hydration on a fresh load is fine.
 *
 * Kept separate from isStaticDestination on purpose: listing these there (as the roles index
 * once was) made onRenderClient skip hydration on the initial load too, which left the whole
 * page chrome inert (the "switching language does nothing on /jobs/roles" bug).
 */
export function isServerDataOnlyDestination(path: string): boolean {
  // Roles index (/jobs/roles, /tyopaikat/ammatit, /lediga-jobb/yrken)
  if (/^\/(en\/jobs\/roles|fi\/tyopaikat\/ammatit|sv\/lediga-jobb\/yrken)\/?$/.test(path)) return true;
  // Employers A to Z directory
  if (/^\/(en\/employers\/a-z|fi\/tyonantajat\/aakkosittain|sv\/alla-arbetsgivare\/a-o)(\/|$)/.test(path)) return true;
  return false;
}

/** True when a link to `path` must be a full page load rather than a soft navigate(). */
export function requiresFullPageLoad(path: string): boolean {
  return isStaticDestination(path) || isServerDataOnlyDestination(path);
}
